// Fleet-wide live telemetry for the portfolio dashboard cards (#565).
// One socket for the whole page: joins every visible site's room and folds
// telemetry:batch site_summary payloads into a per-site map using the same
// per-key merge contract as useDelayedSiteSummary (djin-cloud#372).
// nowMs is a shared 10 s ticker so cards can compute staleness without
// per-card timers.

import { useEffect, useState } from 'react';
import { socketService } from '../services/socket';

export interface FleetLiveEntry {
  summary: Record<string, unknown>;
  lastUpdatedAt: number;
}

interface TelemetryBatchData {
  site_id: string;
  site_summary?: Record<string, unknown>;
}

const STALE_TICK_MS = 10_000;

export function useFleetTelemetry(siteIds: string[]): {
  liveBySite: Map<string, FleetLiveEntry>;
  nowMs: number;
} {
  const [liveBySite, setLiveBySite] = useState<Map<string, FleetLiveEntry>>(() => new Map());
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Stable identity for the effect: page/filter changes produce a new array
  // every render; only re-subscribe when the membership actually changes.
  const idsKey = siteIds.slice().sort().join(',');

  useEffect(() => {
    if (!idsKey) return;

    let socket: ReturnType<typeof socketService.connect>;
    try {
      socket = socketService.connect();
    } catch {
      // No auth token yet — socket.io reconnection isn't in play; the cards
      // stay in the 'connecting' state until a later mount succeeds.
      return;
    }

    const ids = idsKey.split(',');
    const join = () => ids.forEach((id) => socket.emit('subscribe:site', id));
    join();
    socket.on('connect', join);

    const handler = (data: unknown) => {
      if (!data || typeof data !== 'object') return;
      const batch = data as TelemetryBatchData;
      if (!batch.site_id || !batch.site_summary) return;
      if (!ids.includes(batch.site_id)) return;
      setLiveBySite((prev) => {
        const next = new Map(prev);
        const existing = next.get(batch.site_id);
        next.set(batch.site_id, {
          summary: { ...(existing?.summary ?? {}), ...batch.site_summary },
          lastUpdatedAt: Date.now(),
        });
        return next;
      });
    };
    socket.on('telemetry:batch', handler);

    return () => {
      socket.off('telemetry:batch', handler);
      socket.off('connect', join);
      ids.forEach((id) => socket.emit('unsubscribe:site', id));
    };
  }, [idsKey]);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), STALE_TICK_MS);
    return () => clearInterval(t);
  }, []);

  return { liveBySite, nowMs };
}
